/* eslint-disable eqeqeq */
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import { memo, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useQuery } from 'react-query';
import { useReactToPrint } from 'react-to-print';
import { NumericFormat } from 'react-number-format';

import { currencyTypeSelectOptions } from 'shared/selectOptionsList';
import HOME_TYPE from 'shared/homeTypeList';
import useCurrency from 'hooks/useCurrency';
import REPAIR_TYPE from 'shared/repairTypeList';
import useMenu from 'hooks/useMenu';
import useAccess from 'hooks/useAccess';
import CURRENCY_TYPE from 'shared/currencyTypeList';
import { fromTextToBase64parser } from 'utils/textParser';
import { formatMultiplies } from 'utils/formatOddDecimal';
import OrderAddModal from 'pages/admin/order/OrderAddModal';
import ShowroomHomeDetailToPrint from './ShowroomHomeDetailToPrint';
import CalculationByHomeData from './calculation/CalculationByHomeData';
import EditablePrintModal from './prints/EditablePrintModal';
import { useAppInfo } from 'context';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import SimpleToggleButtonGroup from 'components/ui/simple-fields/toggle-button-group/SimpleToggleButtonGroup';

const ShowroomMoreHomeDetailsModal = ({
  open,
  setOpen,
  homesData = [],
  object = {},
  setRefetch = () => {},
}) => {
  const componentRef = useRef();
  const { hasChild } = useMenu();
  const { hasAccess } = useAccess();
  const axiosPrivate = useAxiosPrivate();
  const [{ info }] = useAppInfo();
  const [hasError, setHasError] = useState(false);
  const [isCreditTable, setIsCreditTable] = useState(false);
  const [isSubsidy, setIsSubsidy] = useState(false);
  const { t } = useTranslation();
  const [openOrderModal, setOpenOrderModal] = useState(false);
  const [openPrintModal, setOpenPrintModal] = useState(false);
  const [filters, setFilters] = useState({ month: '', payment: '' });
  const [designType, setDesignType] = useState(
    localStorage.getItem('more-info-type') || 'old',
  );
  const [searchParam] = useSearchParams();
  const object_id = searchParam.get('objectId');
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState({});

  const allSquare = useMemo(() => {
    const onlyHome = homesData?.reduce((acc, curr) => acc + +curr?.square, 0);
    if (homesData?.[0]?.issquareprice == 1) {
      return onlyHome;
    }

    const withTerassa = homesData?.reduce(
      (acc, curr) => acc + +curr?.square + +curr?.terassa,
      0,
    );
    return { onlyHome, withTerassa };
  }, [homesData]);

  const homeData = useMemo(() => {
    if (homesData?.[0]?.issquareprice == 1)
      return { ...homesData?.[0], square: allSquare };

    const newAllPrice = homesData?.reduce((ac, cur) => {
      if (cur?.issquareprice == 0) return ac + +cur?.totalsum;
      return (
        ac +
        (cur?.repaired || cur?.norepaired) * cur?.square +
        +cur?.terassa * (+cur?.terassa_price || +cur?.terassa_price_repaired)
      );
    }, 0);
    const newPrice = newAllPrice / allSquare?.withTerassa;
    const newData = {
      repaired: newPrice,
      norepaired: newPrice,
      deal_price: newPrice,
      terassa_price: newPrice,
      terassa_price_repaired: newPrice,
    };
    return { ...homesData?.[0], ...newData, square: allSquare?.onlyHome };
  }, [homesData, allSquare]);

  const formik = useFormik({
    initialValues: { discountId: '' },
    validationSchema: yup.object({}),
  });
  const fPrice =
    homeData?.isrepaired == REPAIR_TYPE.REPAIRED.code
      ? homeData?.repaired && homeData?.square
        ? formatMultiplies([homeData.repaired, homeData.square], 2)
        : 0
      : homeData?.norepaired && homeData?.square
        ? formatMultiplies([homeData.norepaired, homeData.square], 2)
        : 0;
  const { currencyData } = useCurrency();
  const [currency, setCurrency] = useState(
    homeData?.isvalute || CURRENCY_TYPE.SUM.code,
  );
  const [homeCalculations, setHomeCalculations] = useState({
    isvalute: CURRENCY_TYPE.SUM.code,
    isrepaired: homeData?.isrepaired,
    fullPrice: homeData?.price,
    terassasum: 0,
    sellPrice: 0,
    startPrice: 0,
    startPricePercentage: 0,
    month: 0,
    priceForMonth: 0,
    priceForSquare: 0,
    discount: 0,
    discountPercentage: 0,
    itemKey: 0,
    lastPayment: 0,
  });

  const { data: selectedHome } = useQuery({
    queryKey: [
      '/admin/variables/objectvariables?page=OBJECT&object_id={object_id}',
      object?.id,
    ],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/admin/variables/objectvariables?page=OBJECT&object_id=${object?.id}`,
      );
      return response.data.data;
    },
    enabled: !hasError && !!object?.id,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `${info?.name ? info?.name + ' ' : ''}${object?.name}_${
      homeData?.blocks?.name
    }_${homeData?.number} uy`,
    pageStyle: `
    @media print {
      @page { size: portrait; margin: 0mm;  }
    }
    body {
        -webkit-print-color-adjust: exact;
    }`,
  });

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeDesign = (val) => {
    setDesignType(val);
    localStorage.setItem('more-info-type', val);
  };

  const setHomeCalculationByIndex = (value) => {
    setHomeCalculations((prev) => ({
      ...prev,
      ...value,
    }));
  };

  const handleCurrencyTypeChange = (value) => {
    if (currencyData && currencyData.sum && currency != value) {
      setCurrency(value);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      // maxWidth='xl'
      fullScreen
      sx={{ zIndex: 900 }}
      PaperProps={{
        className: '!shadow-none !rounded-[5px] !max-w-[1440px] min-w-[1350px]',
      }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!bg-[#CDCDCDC7] !text-center !border-b !border-b-[#E6E6E6]'
      >
        <div className='flex items-center justify-center gap-2 relative'>
          <span className='pr-5 font-semibold text-[21px] text-[#374957]'>
            {/* Xonadon ma'lumotlari */}
            {t('shaxmatka.moreHomeDetails.homeInfos')}
          </span>
          <div>
            <SimpleToggleButtonGroup
              delay={0}
              duration={0}
              value={designType}
              changeFn={handleChangeDesign}
              options={[
                { code: 'old', label: 'Eski dizayn' },
                { code: 'new', label: 'Yangi dizayn' },
              ]}
              size='small'
              fullWidth={true}
              buttonClassName='whitespace-nowrap'
            />
          </div>
          <div className='home-details-header !items-center absolute right-0'>
            {/* {t('shaxmatka.moreHomeDetails.selectedHomes')} */}
            <div className='currency-data flex-nowrap flex items-center'>
              <label className='flex items-center font-medium text-base mr-2'>
                <Checkbox
                  checked={isCreditTable}
                  onChange={(e) => setIsCreditTable(e?.target?.checked)}
                  size='small'
                />
                kr
              </label>
              <label className='flex items-center font-medium text-base mr-2'>
                <Checkbox
                  checked={isSubsidy}
                  onChange={(e) => setIsSubsidy(e?.target?.checked)}
                  size='small'
                />
                sb
              </label>
              {currency == CURRENCY_TYPE.VALUTE.code && (
                <div className='mr-2 whitespace-nowrap text-sm'>
                  {t('common.fields.currency')}:{' '}
                  <NumericFormat
                    value={currencyData?.sum}
                    allowNegative={false}
                    displayType={'text'}
                    thousandSeparator={' '}
                    decimalScale={1}
                    className='w-full text-right'
                    suffix=' UZS'
                  />
                </div>
              )}
              <SimpleToggleButtonGroup
                delay={0}
                duration={0}
                value={currency?.toString()}
                changeFn={handleCurrencyTypeChange}
                options={currencyTypeSelectOptions}
                size='small'
                fullWidth={true}
                buttonClassName='whitespace-nowrap'
              />
            </div>
          </div>
        </div>
      </DialogTitle>

      <DialogContent className='!bg-[#F8FAFC]'>
        {homeData && Object.keys(homeData).length > 0 && (
          <div className='shaxmatka-more-home-details-wrapper !w-full !border-none'>
            <CalculationByHomeData
              homeData={{
                ...homeData,
                isrepaired: homeCalculations?.isrepaired,
              }}
              object={object}
              filters={filters}
              setFilters={setFilters}
              homesData={homesData}
              setData={setHomeCalculationByIndex}
              formik={formik}
              applyDiscount={applyDiscount}
              setApplyDiscount={setApplyDiscount}
              selectedDiscount={selectedDiscount}
              setSelectedDiscount={setSelectedDiscount}
              designType={designType}
              currency={currency}
              isCreditTable={isCreditTable}
              isSubsidy={isSubsidy}
            />
            <div className='action-buttons-wrapper'>
              <div>
                <div className='hidden'>
                  <ShowroomHomeDetailToPrint
                    filters={filters}
                    ref={componentRef}
                    applyDiscount={applyDiscount}
                    selectedDiscount={selectedDiscount}
                    formik={formik}
                    selectedHome={selectedHome}
                    homeData={{
                      ...homeData,
                      isrepaired: homeCalculations?.isrepaired,
                      repaired: homeData?.repaired,
                      norepaired: homeData?.norepaired,
                      rooms: homeData?.rooms,
                      islive: homeData?.islive,
                      stage: homeData?.stage,
                      number: homeData?.number,
                      status: homeData?.status,
                      plan: homeData?.plan,
                      square: homeData?.square,
                      fullPriceSum:
                        homeData?.isvalute == CURRENCY_TYPE.SUM.code
                          ? fPrice
                          : fPrice * currencyData.sum,
                      fullPriceValute:
                        homeData?.isvalute == CURRENCY_TYPE.SUM.code
                          ? fPrice / currencyData.sum
                          : fPrice,
                      calculations: homeCalculations,
                    }}
                    blockName={homeData?.blocks?.name}
                    objectData={object}
                    isCreditTable={isCreditTable}
                    isSubsidy={isSubsidy}
                    calculations={homeCalculations?.calculations}
                    subsidyCals={homeCalculations?.subsidyCals}
                  />
                </div>
                <Button
                  variant='outlined'
                  color='success'
                  onClick={() => handlePrint()}
                  className='h-9'
                  size='small'
                >
                  <i className='bi bi-printer text-lg mr-1' />
                  {/* Chop etish */}
                  {t('common.button.print')}
                </Button>
                <Button
                  variant='outlined'
                  color='success'
                  onClick={() => setOpenPrintModal(true)}
                  className='!ml-2 h-9'
                  size='small'
                >
                  <i className='bi bi-printer text-lg mr-1' />
                  {/* Chop etish */}
                  {t('common.button.print')}
                  <span className='py-1 px-1 ml-1 rounded bg-base-color-light text-[10px] leading-3 italic text-white font-medium'>
                    Beta
                  </span>
                </Button>
              </div>
              <div className='flex items-center'>
                {homeData?.status &&
                  homeData.status == HOME_TYPE.ACTIVE.code &&
                  hasAccess(object_id) &&
                  hasChild?.('SHOWROOM', 'ORDER_HOMES') && (
                    <Button
                      variant='contained'
                      color='warning'
                      className='h-9'
                      size='small'
                      onClick={() => setOpenOrderModal(true)}
                    >
                      {/* Buyurtma berish */}
                      {t('order.view.createOrder')}
                    </Button>
                  )}

                {homeData?.status &&
                  homeData.status == HOME_TYPE.ACTIVE.code &&
                  hasAccess(object_id) &&
                  hasChild?.('SHOWROOM', 'ADD_CONTRACT') && (
                    <Button
                      variant='contained'
                      color='primary'
                      className='h-9 !ml-2 !p-0'
                      size='small'
                    >
                      <Link
                        to={`/admin/contract/add-from-shaxmatka?homeData=${fromTextToBase64parser(
                          JSON.stringify({
                            ...homeData,
                            isrepaired: homeCalculations?.isrepaired,
                          }),
                        )}&calculationData=${fromTextToBase64parser(
                          JSON.stringify(homeCalculations),
                        )}&homes=${fromTextToBase64parser(
                          JSON.stringify({
                            ids: homesData?.map(({ id }) => ({ home_id: id })),
                            homesData: homesData?.map((data) => ({
                              ...data,
                              padezd: null,
                              entrance: { ...data?.entrance, homes: null },
                            })),
                          }),
                        )}`}
                        state={{ allowed: true }}
                        className='no-underline py-1.5 px-3.5 whitespace-nowrap'
                      >
                        {/* Shartnoma tuzish */}
                        {t('order.view.createContract')}
                      </Link>
                    </Button>
                  )}
              </div>
            </div>
          </div>
        )}
        <EditablePrintModal
          open={openPrintModal}
          setOpen={setOpenPrintModal}
          selectedHome={selectedHome}
          homeData={{
            ...homeData,
            repaired: homeData?.repaired,
            norepaired: homeData?.norepaired,
            rooms: homeData?.rooms,
            islive: homeData?.islive,
            stage: homeData?.stage,
            number: homeData?.number,
            status: homeData?.status,
            plan: homeData?.plan,
            square: homeData?.square,
            isrepaired: homeCalculations?.isrepaired,
            fullPriceSum:
              homeData?.isvalute == CURRENCY_TYPE.SUM.code
                ? fPrice
                : fPrice * currencyData.sum,
            fullPriceValute:
              homeData?.isvalute == CURRENCY_TYPE.SUM.code
                ? fPrice / currencyData.sum
                : fPrice,
            calculations: homeCalculations,
          }}
          blockName={homeData?.blocks?.name}
          objectData={object}
          applyDiscount={applyDiscount}
          setApplyDiscount={setApplyDiscount}
          discountId={formik.values?.discountId || null}
          setData={setHomeCalculationByIndex}
          selectedDiscount={selectedDiscount}
          setSelectedDiscount={setSelectedDiscount}
          filters={filters}
          isCreditTable={isCreditTable}
          isSubsidy={isSubsidy}
          calculations={homeCalculations?.calculations}
          subsidyCals={homeCalculations?.subsidyCals}
        />
        {openOrderModal && hasChild?.('SHOWROOM', 'ORDER_HOMES') && (
          <OrderAddModal
            open={openOrderModal}
            setOpen={setOpenOrderModal}
            data={homeData}
            setRefetch={setRefetch}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(ShowroomMoreHomeDetailsModal);
